import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { MarketDivider } from '@market/react';
import { Options } from 'typescript/components/option_rows/option_rows';
import { buildOptionFromPickupOption } from 'typescript/utilities/pickup_options';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import OptionRowsWithLogging from 'typescript/apps/checkout_v3/components/option_rows_with_logging';
import EmptyStateWithLoading from 'typescript/components/empty_state_with_loading';
import styles from 'typescript/apps/checkout_v3/components/delivery/bopis/pickup_options_finder.module.scss';
import {
  useListPickupOptions,
  useReservePickupLocation,
} from 'typescript/hooks/use_bopis_client';
import {
  COMPONENT_NAMES,
  CHECKOUT_PAGE_CLICK_EVENTS,
} from 'typescript/constants/tracking';
import { POSTAL_CODE_REGEX_BY_COUNTRY } from 'constants/patterns';
import { CountryCode } from 'typescript/constants/localization';
import PostalCodeSearchInput from 'typescript/components/postal_code_search_input';

export const SEARCH_POSTAL_CODE_TEST_ID = 'search-postal-code-test-id';
export const PICKUP_OPTIONS_TEST_ID = 'pickup-options-test-id';

const DEFAULT_SEARCH_POSTAL_CODE = '10001';

const PickupOptionsFinder = ({
  chosenPickupLocationId,
  setChosenPickupLocationId,
}) => {
  const { t } = useTranslation();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });

  const [searchPostalCode, setSearchPostalCode] = useState(
    orderSummaryData?.search_postal_code || DEFAULT_SEARCH_POSTAL_CODE,
  );

  const { pickupOptions, getPickupOptions, isPending } = useListPickupOptions({
    orderNumber,
  });

  const { reservePickupLocation, reservedPickupLocationId } =
    useReservePickupLocation({ orderNumber });

  /**
   * This section handles listing the pickup options
   */

  // prefetch pickup options on initial render
  useEffect(() => {
    if (POSTAL_CODE_REGEX_BY_COUNTRY[CountryCode.US].test(searchPostalCode)) {
      getPickupOptions({ postalCode: searchPostalCode });
    }
  }, []);

  // fetch pickup options on user action
  const handlePostalCodeChange = (newPostalCode) => {
    // Only fetch with a valid postal code that's different from the current one
    if (
      POSTAL_CODE_REGEX_BY_COUNTRY[CountryCode.US].test(newPostalCode) &&
      newPostalCode !== searchPostalCode
    ) {
      onCheckoutPageClick({
        eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.pickup_postal_code_change,
        trackingData,
      });
      setSearchPostalCode(newPostalCode);
      setChosenPickupLocationId(null);
      getPickupOptions({ postalCode: newPostalCode });
    }
  };

  /**
   * This section handles reserving the selected pickup option
   * Reservation is needed on every change to update tax displayed
   * and helps with preserving the user's selection
   */

  // reserve the user selected option, but update chosenPickupLocationId
  // immediately to render selection before API returns
  const onPickupLocationSelect = ({ optionId }) => {
    onCheckoutPageClick({
      eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.pickup_option_select,
      trackingData,
    });
    setChosenPickupLocationId(optionId);
    reservePickupLocation({
      chosenPickupLocationId: optionId,
      searchPostalCode,
    });
  };

  // when the API responds, update chosenPickupLocationId state to trigger
  // a re-render only if the API response is different from the current state
  // this would happen only if the reservation failed
  useEffect(() => {
    if (
      !reservedPickupLocationId ||
      reservedPickupLocationId === chosenPickupLocationId
    ) {
      return;
    }

    setChosenPickupLocationId(reservedPickupLocationId);
  }, [reservedPickupLocationId]);

  /**
   * This method combines the two elements showing the list and the selection
   */

  // build the options for the OptionRows component any time pickupOptions change
  // (searching a new postal code) or when chosenPickupLocationId changes
  // (user selects a new option or reservation response has new value)
  const pickupLocationOptions: Options[] = useMemo(() => {
    if (!pickupOptions?.length) return null;
    if (isPending) return []; // [] will show loading spinner

    return pickupOptions.map((pickupOption, _index) =>
      buildOptionFromPickupOption({
        pickupOption,
        chosenPickupLocationId: chosenPickupLocationId?.toString(),
      }),
    );
  }, [pickupOptions, chosenPickupLocationId]);

  return (
    <div className="shop-grid-container shop-grid-item-full">
      <PostalCodeSearchInput
        value={searchPostalCode}
        onValueChange={handlePostalCodeChange}
        showSearchButton={false}
        className="shop-grid-item-full"
        inputTestId={SEARCH_POSTAL_CODE_TEST_ID}
      />
      {pickupLocationOptions && !isPending ? (
        <div className="shop-grid-container shop-grid-item-full no-grid-gap">
          <OptionRowsWithLogging
            className="shop-grid-item-full"
            dataTestId={PICKUP_OPTIONS_TEST_ID}
            header={t('checkout.delivery.pickup.chooseStore')}
            options={pickupLocationOptions}
            onOptionSelect={onPickupLocationSelect}
            componentName={COMPONENT_NAMES.APPS.CHECKOUT_V3.PICKUP_OPTIONS}
          />
          {pickupLocationOptions?.length > 0 && (
            <div className="shop-grid-item-full">
              <MarketDivider size="thin" />
              <p className={`${styles.disclaimer} shop-grid-item-full`}>
                {t('checkout.delivery.pickup.disclaimer')}
              </p>
            </div>
          )}
        </div>
      ) : (
        <EmptyStateWithLoading
          className={`${styles.noOptions} shop-grid-item-full`}
          showLoading={isPending}
          description={t('checkout.delivery.pickup.noAvailableStores')}
        />
      )}
    </div>
  );
};

export default PickupOptionsFinder;
